import { injectable, inject } from 'inversify';
import { IConfigurationService, ConfigurationKey } from '@/app-modules/config/config-service-interface';
import { IChatModsConfiguration, IGamesResponseViewModel, IGameResponseViewModel, IGameClubViewModel, IGameChatRoomViewModel } from '@/app-modules/config/models';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IHttpService, IHttpRequest } from '@/app-modules/http';
import { IHamsterRaceTicketSetConfigurationViewModel } from '@/app-modules/config/models/hamster-race-ticket-set-response-view-model';
import { container } from '@/app-modules/_ioc';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { UserPreferences, ConnectionTimeoutOverride } from '@/app-modules/store/modules/user-preferences';
import { User } from '@/app-modules/store/modules/user';
import { ITogglesService } from '@/app-modules/config';

class ConfigurationCache<TConfig> {
  private cache: { [key: string]: TConfig } = {};

  public Set(key: string, value: TConfig) {
    this.cache[key] = value;
  }

  public Get(key: string) {
    return this.cache[key];
  }

  public ContainsKey(key: string): boolean {
    return (this.cache[key] !== undefined);
  }

  public Remove(key: string) {
    delete this.cache[key];
  }
}

@injectable()
abstract class ConfigurationBase {
  public getConfigurationItem<T = string>(configurationKey: ConfigurationKey): T {
    const currentConfig = this.getUserPreferredCountry();
    const env = process.env[currentConfig] as NodeJS.IApplicationConfiguration;
    return env[configurationKey] as T;
  }

  public getUserPreferredCountry(): string {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const userPreferences = persistedStore.Get(UserPreferences);

    // Split deployment means we need to base the default country depending on uk/int
    const defaultCountryForTenant = !this.currentEnvironmentTenantIsInternational() ? 'uk' : 'es';
    return (this.handleCurrentUserCountry(userPreferences) || defaultCountryForTenant).toLowerCase();
  }

  public currentEnvironmentTenantIsInternational(): boolean {
    const tenantKey = 'tenant';
    return process.env[tenantKey] as string === 'int';
  }

  public getJwt(): string {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const user = persistedStore.Get(User);
    return user.jwt;
  }

  public getWebSocketOverrideBehaviour(): ConnectionTimeoutOverride | undefined {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const preferences = persistedStore.Get(UserPreferences);
    if (preferences) {
      const overrides = preferences.connectionTimeoutOverrides;
      if (overrides && overrides.overrideEnabled) {
        return overrides;
      }
    }
  }

  private handleCurrentUserCountry(userPreferences: UserPreferences) {
    // If an Int user somehow has UK as their preferred country, we need to force them over to the Int default
    if (userPreferences.country === 'uk' && this.currentEnvironmentTenantIsInternational()) {
      return 'es';
    }
    return userPreferences.country;
  }
}

@injectable()
class ConfigurationService extends ConfigurationBase implements IConfigurationService {
  constructor(@inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.ITogglesService) private readonly toggleService: ITogglesService) {
    super();
  }

  private static readonly configurationCache = new ConfigurationCache<IChatModsConfiguration>();
  private static readonly gameConfigurationCache = new ConfigurationCache<IGamesResponseViewModel>();
  private static hamsterRaceTicketSetConfig: IHamsterRaceTicketSetConfigurationViewModel | null = null;

  public async getConfiguration(): Promise<IChatModsConfiguration> {
    const userPreferredCountry = this.getUserPreferredCountry();
    if (ConfigurationService.configurationCache.ContainsKey(userPreferredCountry)) {
      return ConfigurationService.configurationCache.Get(userPreferredCountry);
    }
    const configData = await this.getConfigurationFromServer();
    ConfigurationService.configurationCache.Set(userPreferredCountry, configData);
    return configData;
  }

  public getConfigurationSync(): IChatModsConfiguration {
    const userPreferredCountry = this.getUserPreferredCountry();
    return ConfigurationService.configurationCache.Get(userPreferredCountry);
  }

  public async getHamsterRaceTicketSetConfiguration(): Promise<IHamsterRaceTicketSetConfigurationViewModel> {
    if (ConfigurationService.hamsterRaceTicketSetConfig == null && this.getUserPreferredCountry() === 'uk') {
      ConfigurationService.hamsterRaceTicketSetConfig = await this.getHamsterRaceTicketSetConfigurationFromServer();
    }

    return ConfigurationService.hamsterRaceTicketSetConfig;
  }

  public async getGameConfiguration(): Promise<IGamesResponseViewModel> {
    const userPreferredCountry = this.getUserPreferredCountry();
    if (ConfigurationService.gameConfigurationCache.ContainsKey(userPreferredCountry)) {
      return ConfigurationService.gameConfigurationCache.Get(userPreferredCountry);
    }

    const gameConfig = await this.getGameConfigurationFromServer();
    ConfigurationService.gameConfigurationCache.Set(userPreferredCountry, gameConfig);
    return gameConfig;
  }

  private async getHamsterRaceTicketSetConfigurationFromServer(): Promise<IHamsterRaceTicketSetConfigurationViewModel> {
    const httpRequest: IHttpRequest = {
      Url: '/games/hamster-race/ticket-sets'
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest);
    if (result.status !== 200) {
      throw new Error('Could not retrieve Hamster Race configuration.');
    }

    return result.data.Data as IHamsterRaceTicketSetConfigurationViewModel;
  }

  private async getGameConfigurationFromServer(): Promise<IGamesResponseViewModel> {
    const httpRequest: IHttpRequest = {
      Url: '/games/all-games'
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);
    if (result.status !== 200 || result.data == null) {
      throw new Error('Could not retrieve game configuration.');
    }
    const games = (result.data as IGamesResponseViewModel).Games || [];
    games.forEach((game) => {
      if (game) {
        game.FrontEndName = game.FrontEndName || game.Name;
      }
    });
    return this.sortGames(result.data as IGamesResponseViewModel);
  }

  private async handleUnifiedConfigurationV3(result: any): Promise<IChatModsConfiguration> {
    const config = JSON.parse(result.data.data) as IChatModsConfiguration;
    return Object.assign({}, config, {
      getConfigurationByPath(path: string): string {
        if (!path.startsWith('/')) {
          path = `/${path}`;
        }
        path = 'ui' + path;
        return (this as IChatModsConfiguration)[path];
      }
    });
  }

  private async getConfigurationFromServer(): Promise<IChatModsConfiguration> {
    const httpRequest: IHttpRequest = {
      Url: '/configuration/ui'
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);
    if (result.status !== 200) {
      throw new Error('Could not retrieve configuration.');
    }

    return await this.handleUnifiedConfigurationV3(result);
  }

  private sortGames(gameConfig: IGamesResponseViewModel): IGamesResponseViewModel {
    gameConfig.Games = gameConfig.Games.sort(this.compareGameEntities);
    gameConfig.Games.forEach((game) => this.sortGameClubs(game));
    return gameConfig;
  }

  private sortGameClubs(game: IGameResponseViewModel): void {
    game.Clubs = game.Clubs.sort(this.compareGameEntities);
  }

  private compareGameEntities(entity: IGameResponseViewModel | IGameClubViewModel | IGameChatRoomViewModel, entityToCompare: IGameResponseViewModel | IGameClubViewModel | IGameChatRoomViewModel): number {
    const entityName = entity.Name.toLowerCase();
    const entityToCompareName = entityToCompare.Name.toLowerCase();
    return entityName.localeCompare(entityToCompareName);
  }
}

export { ConfigurationBase, ConfigurationService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/config/config-service.ts